import React, { useMemo, useState, useEffect, useRef } from "react";
import { useI18n } from "../contexts/I18nContext";
import { X, Download, FileText, FileType, Share2, Copy, Check } from "lucide-react";
import { API_BASE, getAccess, api } from "../lib/api";
import { toast } from "sonner";

const ORDER = [
  "executive_summary", "facts", "legal_issues", "applicable_cyprus_law",
  "case_law", "analysis", "counterarguments", "risks", "next_steps",
  "sources", "disclaimer",
];

function renderSection(key, value) {
  if (value == null || value === "") return <div className="text-slate-400 text-sm">—</div>;
  if (typeof value === "string") {
    return <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{value}</p>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <div className="text-slate-400 text-sm">—</div>;
    // array of strings
    if (typeof value[0] === "string") {
      return (
        <ul className="space-y-2 list-disc ml-5 text-slate-700 leading-relaxed">
          {value.map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      );
    }
    // array of objects
    return (
      <div className="space-y-3">
        {value.map((v, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            {Object.entries(v).map(([k, val]) => (
              <div key={k} className="text-sm flex gap-2">
                <span className="uppercase text-[10px] tracking-[0.18em] font-bold text-[#8B1E3F] min-w-[80px]">{k.replace(/_/g, " ")}</span>
                <span className="text-slate-700 flex-1">{String(val)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return <pre className="text-xs bg-slate-50 p-3 rounded">{JSON.stringify(value, null, 2)}</pre>;
}

export default function ReportViewer({ report, onClose }) {
  const { t, lang } = useI18n();
  const p = report?.payload || {};
  const confidenceColor = { low: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", high: "bg-emerald-100 text-emerald-700" }[p.confidence] || "bg-slate-100 text-slate-600";

  const download = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `report-${report.id}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadFormat = async (fmt) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("dk_access");
      const res = await fetch(`${backend}/api/reports/${report.id}/export?format=${fmt}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dikigoros-report-${report.id.slice(0, 8)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export error:", e);
      alert("Export failed. Please try again.");
    }
  };

  const sections = useMemo(() => ORDER.map((k) => ({ key: k, title: t(`report.${k}`), value: p[k] })), [p, t]);

  // ---------- Active section tracking (sidebar highlight) ----------
  const [activeSection, setActiveSection] = useState(ORDER[0]);
  const scrollRef = useRef(null);
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top that is intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          const id = visible[0].target.id.replace(/^sec-/, "");
          if (id) setActiveSection(id);
        }
      },
      { root, rootMargin: "0px 0px -65% 0px", threshold: [0, 0.15, 1] },
    );
    ORDER.forEach((k) => {
      const el = document.getElementById(`sec-${k}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [report.id]);

  const scrollToSection = (key) => {
    const el = document.getElementById(`sec-${key}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ---------- Share state ----------
  const [shareToken, setShareToken] = useState(report.share_token || null);
  const [isShared, setIsShared] = useState(!!report.is_shared);
  const [copied, setCopied] = useState(false);
  const [copyFallback, setCopyFallback] = useState(false);  // both strategies failed → show Ctrl+C hint

  useEffect(() => {
    setShareToken(report.share_token || null);
    setIsShared(!!report.is_shared);
  }, [report.id, report.share_token, report.is_shared]);

  const toggleShare = async () => {
    try {
      const res = await api.post(`/reports/${report.id}/share`, { enabled: !isShared });
      setShareToken(res.data.share_token || null);
      setIsShared(!!res.data.is_shared);
      if (res.data.is_shared) toast.success(t("report.shareEnabled"));
      else toast.success(t("report.shareRevoked"));
    } catch (e) {
      toast.error(t("report.shareError"));
    }
  };

  const publicUrl = shareToken ? `${window.location.origin}/share/r/${shareToken}` : "";
  const copyLink = async () => {
    if (!publicUrl) return;
    setCopyFallback(false);
    // Try modern clipboard API first
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(publicUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        return;
      }
    } catch (_) { /* fall through to legacy */ }
    // Legacy fallback: hidden textarea + execCommand
    try {
      const ta = document.createElement("textarea");
      ta.value = publicUrl;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } else {
        // Both strategies failed — show inline Ctrl+C hint + focus input
        setCopyFallback(true);
        setTimeout(() => setCopyFallback(false), 5000);
        const input = document.querySelector('[data-testid="share-link-url"]');
        if (input) { input.focus(); input.select && input.select(); }
      }
    } catch (e) {
      setCopyFallback(true);
      setTimeout(() => setCopyFallback(false), 5000);
      const input = document.querySelector('[data-testid="share-link-url"]');
      if (input) { input.focus(); input.select && input.select(); }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex justify-end" data-testid="report-viewer">
      <div className="relative bg-white w-full md:w-[min(1120px,95vw)] h-full overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">Dikigoros.io Report</div>
            <div className="mt-1 font-display font-bold text-xl text-slate-900 line-clamp-1">{report.question}</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${confidenceColor}`} data-testid="report-confidence">
              {t("report.confidence")}: {p.confidence || "—"}
            </span>
            <button onClick={toggleShare} data-testid="report-share-toggle"
                    className={`rounded-full px-3 py-1.5 text-xs inline-flex items-center gap-1 transition border ${
                      isShared ? "bg-[#8B1E3F] text-white border-[#8B1E3F]" : "border-slate-200 hover:bg-slate-50 text-slate-700"
                    }`}>
              <Share2 className="h-3.5 w-3.5" /> {isShared ? t("report.shared") : t("report.share")}
            </button>
            <button onClick={() => downloadFormat("pdf")} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-1" data-testid="report-export-pdf">
              <FileText className="h-3.5 w-3.5" /> PDF
            </button>
            <button onClick={() => downloadFormat("docx")} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-1" data-testid="report-export-docx">
              <FileType className="h-3.5 w-3.5" /> DOCX
            </button>
            <button onClick={download} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs hover:bg-slate-50 inline-flex items-center gap-1" data-testid="report-download">
              <Download className="h-3.5 w-3.5" /> JSON
            </button>
            <button onClick={onClose} className="rounded-full border border-slate-200 p-2 hover:bg-slate-50" data-testid="report-close">
              <X className="h-4 w-4 text-slate-500" />
            </button>
          </div>
        </div>

        {isShared && publicUrl && (
          <div className="px-6 md:px-10 py-3 bg-[#8B1E3F]/5 border-b border-slate-200 flex items-center gap-3 flex-wrap" data-testid="share-link-bar">
            <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#8B1E3F]">{t("report.publicLink")}</span>
            <input type="text" readOnly value={publicUrl}
                   onFocus={(e) => e.target.select()}
                   className="text-xs text-slate-700 bg-white border border-slate-200 rounded-full px-3 py-1 truncate max-w-md flex-1 min-w-[220px] font-mono focus:outline-none focus:ring-2 focus:ring-[#8B1E3F]/40"
                   data-testid="share-link-url" />
            <button onClick={copyLink} className="rounded-full border border-slate-200 px-3 py-1 text-xs bg-white hover:bg-slate-50 inline-flex items-center gap-1"
                    data-testid="share-link-copy">
              {copied ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> {t("report.copied")}</> : <><Copy className="h-3.5 w-3.5" /> {t("report.copy")}</>}
            </button>
            {copyFallback && (
              <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 inline-flex items-center gap-1 animate-pulse"
                    data-testid="copy-hint">
                <kbd className="font-mono text-[10px] bg-white/80 border border-amber-300 rounded px-1">Ctrl</kbd>
                <span>+</span>
                <kbd className="font-mono text-[10px] bg-white/80 border border-amber-300 rounded px-1">C</kbd>
                <span className="ml-1">{t("report.copyHint")}</span>
              </span>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto" ref={scrollRef}>
          <div className="grid lg:grid-cols-12 gap-10 p-6 md:p-10">
            <aside className="lg:col-span-3 lg:sticky lg:top-0 lg:self-start lg:max-h-[calc(100vh-96px)] lg:overflow-y-auto">
              <div className="kicker mb-3">{lang === "el" ? "Πλοήγηση" : "Outline"}</div>
              <nav className="space-y-1" data-testid="report-nav">
                {sections.map(({ key, title }, i) => {
                  const active = activeSection === key;
                  return (
                    <button
                      key={key}
                      onClick={() => scrollToSection(key)}
                      data-testid={`report-nav-${key}`}
                      className={`w-full text-left text-sm transition rounded-lg px-3 py-2 flex items-center gap-2 ${
                        active
                          ? "bg-[#8B1E3F]/10 text-[#0A192F] font-semibold border-l-2 border-[#8B1E3F]"
                          : "text-slate-600 hover:text-[#0A192F] hover:bg-slate-50 border-l-2 border-transparent"
                      }`}
                    >
                      <span className={`font-mono text-[10px] w-6 ${active ? "text-[#8B1E3F] font-bold" : "text-slate-400"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 line-clamp-1">{title}</span>
                    </button>
                  );
                })}
              </nav>
              {p.uncertainty_notes && (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800" data-testid="report-uncertainty">
                  <div className="font-semibold uppercase tracking-wider text-[10px] mb-1">{t("report.uncertaintyNotes")}</div>
                  {Array.isArray(p.uncertainty_notes) ? (
                    <ul className="list-disc ml-4 space-y-1">{p.uncertainty_notes.map((u, i) => <li key={i}>{String(u)}</li>)}</ul>
                  ) : (
                    p.uncertainty_notes
                  )}
                </div>
              )}
            </aside>

            <article className="lg:col-span-9 space-y-12">
              {sections.map(({ key, title, value }, i) => (
                <section key={key} id={`sec-${key}`} className="report-section" data-testid={`section-${key}`}>
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-display font-bold text-2xl md:text-3xl text-slate-900 tracking-tight">{title}</h2>
                  </div>
                  <div className="mt-4">{renderSection(key, value)}</div>
                </section>
              ))}
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}
